import { useState, useEffect, useMemo, useRef } from 'react';
import { useError } from '@app/hooks/useError';
import { ForecastModel, FORECAST_MODELS } from '@app/common/static-data/models';
import Logger from '@app/utils/logger';
import { JuaForecastRequest } from '@app/api/forecasting/types';
import { useAccess } from '@app/components/access';
import { useDemo } from '@app/components/demo/DemoProvider';

import { useForecastApi } from './useForecastApi';
import { PlottableForecast } from '../types';

type ModelId = (typeof FORECAST_MODELS)[number]['id'];

interface Location {
  lat: number;
  lon: number;
  name: string;
}

interface UseForecastDataProps {
  location: Location;
  selectedModels: ForecastModel[];
  endHour: number;
  startHour?: number;
  selectedInitTimes?: Record<string, string>;
}

export function useForecastData({
  location,
  selectedModels,
  endHour,
  startHour = 0,
  selectedInitTimes = {},
}: UseForecastDataProps) {
  const { access, isLoading: isAccessLoading } = useAccess();
  const { isDemo } = useDemo();
  const [forecastData, setForecastData] = useState<Record<ModelId, PlottableForecast>>(
    {} as Record<ModelId, PlottableForecast>,
  );
  const [loading, setLoading] = useState(false);
  const { showError } = useError();
  const { fetchForecast } = useForecastApi();

  // Memoize available models to prevent unnecessary re-renders
  const availableModels = useMemo(() => {
    if (isAccessLoading || !access) {
      return [];
    }

    if (isDemo) {
      return FORECAST_MODELS.map((m) => ({ ...m, available: m.provider === 'jua' }));
    }

    if (!access.permissions.has('read:forecasts')) {
      return [];
    }

    const visible_models = FORECAST_MODELS.filter(
      (m) => access.features.has('ept2') || m.id != 'ept2',
    );
    const available_model_names = access.resources.get('read:forecasts') || new Set();
    return visible_models.map((val) => ({ ...val, available: available_model_names.has(val.id) }));
  }, [access, isAccessLoading]);

  // Memoize selected models string for stable dependency
  const selectedModelsKey = useMemo(() => selectedModels.join(','), [selectedModels]);

  // Memoize init times string for stable dependency
  const selectedInitTimesKey = useMemo(
    () => JSON.stringify(selectedInitTimes),
    [selectedInitTimes],
  );

  // Memoize location for stable dependency
  const locationKey = useMemo(
    () => `${location.lat},${location.lon}`,
    [location.lat, location.lon],
  );

  // Track previous values to enable incremental updates using ref to avoid dependency issues
  const previousSelectedModelsRef = useRef<ForecastModel[]>([]);
  const previousLocationKeyRef = useRef<string>('');
  const previousEndHourRef = useRef<number>(0);
  const previousStartHourRef = useRef<number>(0);
  const previousSelectedInitTimesKeyRef = useRef<string>('');

  // Fetch forecast data
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    // Parse memoized values back to usable form
    const currentSelectedModels = selectedModelsKey
      ? (selectedModelsKey.split(',') as ForecastModel[])
      : [];
    const currentSelectedInitTimes = selectedInitTimesKey ? JSON.parse(selectedInitTimesKey) : {};

    const fetchForecasts = async () => {
      if (!isMounted) return;

      // Don't fetch if no models selected
      if (currentSelectedModels.length === 0) {
        setLoading(false);
        setForecastData({} as Record<ModelId, PlottableForecast>);
        previousSelectedModelsRef.current = [];
        previousLocationKeyRef.current = locationKey;
        previousEndHourRef.current = endHour;
        previousStartHourRef.current = startHour;
        previousSelectedInitTimesKeyRef.current = selectedInitTimesKey;
        return;
      }

      // Check if parameters that require full refetch have changed
      const locationChanged = previousLocationKeyRef.current !== locationKey;
      const endHourChanged = previousEndHourRef.current !== endHour;
      const startHourChanged = previousStartHourRef.current !== startHour;
      const requiresFullRefetch = locationChanged || endHourChanged || startHourChanged;

      // Handle init time changes more granularly - only refetch models whose init times changed
      const previousInitTimes = previousSelectedInitTimesKeyRef.current
        ? JSON.parse(previousSelectedInitTimesKeyRef.current)
        : {};
      const modelsWithInitTimeChanges = currentSelectedModels.filter((modelId) => {
        const prevInitTime = previousInitTimes[modelId];
        const currentInitTime = currentSelectedInitTimes[modelId];
        return prevInitTime !== currentInitTime;
      });

      // Determine which models are new (need fetching) and which to remove
      const previousModelsSet = new Set(previousSelectedModelsRef.current);
      const currentModelsSet = new Set(currentSelectedModels);

      let modelsToFetch: ForecastModel[];
      if (requiresFullRefetch) {
        // Location/time range changed - fetch all models
        modelsToFetch = currentSelectedModels;
      } else {
        // Incremental changes - fetch new models + models with init time changes
        const newModels = currentSelectedModels.filter(
          (modelId) => !previousModelsSet.has(modelId),
        );
        const uniqueModelsToFetch = new Set([...newModels, ...modelsWithInitTimeChanges]);
        modelsToFetch = Array.from(uniqueModelsToFetch);
      }

      const modelsToRemove = previousSelectedModelsRef.current.filter(
        (modelId) => !currentModelsSet.has(modelId),
      );

      // If we're only removing models and no parameters changed, just update state without fetching
      if (modelsToFetch.length === 0 && modelsToRemove.length > 0 && !requiresFullRefetch) {
        setForecastData((prevData) => {
          const newData = { ...prevData };
          modelsToRemove.forEach((modelId) => {
            delete newData[modelId as ModelId];
          });
          return newData;
        });
        previousSelectedModelsRef.current = currentSelectedModels;
        return;
      }

      // Only set loading if we're actually fetching new data
      if (modelsToFetch.length > 0) {
        setLoading(true);
      }

      try {
        // Get the set of available models
        const available_model_names = new Set(
          availableModels.filter((m) => m.available).map((m) => m.id),
        );

        // Only fetch forecasts for models that need fetching and are available to the user
        const forecastPromises =
          modelsToFetch.length > 0
            ? modelsToFetch
                .filter((modelId) => available_model_names.has(modelId))
                .map(async (modelId) => {
                  const model = availableModels.find((m) => m.id === modelId);
                  if (!model) return null;

                  try {
                    const initTime = currentSelectedInitTimes[modelId] || 'latest-full';
                    const request: JuaForecastRequest = {
                      points: [{ lat: location.lat, lon: location.lon }],
                      min_lead_time: startHour,
                      max_lead_time: endHour,
                      initTime: initTime,
                      full: initTime !== 'live', // only return non-full forecasts for live init times
                      ensemble_stats: (model as any).ensembleStats || [],
                      demo: isDemo,
                    };

                    const juaResponse = await fetchForecast(modelId, request);
                    return juaResponse ? { modelId, data: juaResponse } : null;
                  } catch (error) {
                    Logger.error(`Failed to fetch forecast for model ${modelId}`, error);
                    showError({
                      code: 'FORECAST_ERROR',
                      message: `Failed to fetch forecast for ${model.label}`,
                      severity: 'error',
                      details: { model: modelId, error },
                      suggestion: 'Try selecting a different model or try again later.',
                    });
                    return null;
                  }
                })
            : [];

        // If we have models to fetch, wait for results and merge with existing data
        if (forecastPromises.length > 0) {
          const results = await Promise.all(forecastPromises);
          if (isMounted) {
            const newForecastResults = results.reduce(
              (acc, result) => {
                if (result) {
                  acc[result.modelId] = result.data;
                }
                return acc;
              },
              {} as Record<ModelId, PlottableForecast>,
            );

            // Handle data merging based on whether we need full refetch
            setForecastData((prevData) => {
              if (requiresFullRefetch) {
                // Clear existing data and use only new results for selected models
                const updatedData = { ...newForecastResults };
                // Remove any models that are no longer selected
                modelsToRemove.forEach((modelId) => {
                  delete updatedData[modelId as ModelId];
                });
                return updatedData;
              } else {
                // Incremental update: merge new results with existing data
                // This handles both new models and models with init time changes
                const updatedData = { ...prevData, ...newForecastResults };
                // Remove models that are no longer selected
                modelsToRemove.forEach((modelId) => {
                  delete updatedData[modelId as ModelId];
                });
                return updatedData;
              }
            });
          }
        } else if (modelsToRemove.length > 0) {
          // Only removing models, already handled above
        } else {
          // No changes needed, but ensure we have the right data structure
          setForecastData((prevData) => {
            const updatedData = { ...prevData };
            modelsToRemove.forEach((modelId) => {
              delete updatedData[modelId as ModelId];
            });
            return updatedData;
          });
        }

        // Update all the ref tracking values
        previousSelectedModelsRef.current = currentSelectedModels;
        previousLocationKeyRef.current = locationKey;
        previousEndHourRef.current = endHour;
        previousStartHourRef.current = startHour;
        previousSelectedInitTimesKeyRef.current = selectedInitTimesKey;
      } catch (error) {
        Logger.error('Failed to fetch forecasts', error);
        showError({
          code: 'FORECAST_ERROR',
          message: 'Failed to fetch weather forecasts',
          severity: 'error',
          suggestion: 'Please try again later.',
        });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Only fetch if we have location and access is loaded
    if (locationKey && !isAccessLoading) {
      fetchForecasts();
    }

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [
    // Stable memoized dependencies
    locationKey,
    selectedModelsKey,
    selectedInitTimesKey,
    endHour,
    startHour,
    isAccessLoading,
    // Required dependencies used inside the effect
    access,
    availableModels,
    showError,
    fetchForecast,

    // Location properties (covered by locationKey but required by linter)
    location.lat,
    location.lon,
  ]);

  return {
    forecastData,
    loading,
    availableModels,
  };
}
